import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, CalendarDays, Users, Gauge } from "lucide-react";
import Nav from "@/components/site/Nav";
import Footer from "@/components/site/Footer";
import { Reveal, ParallaxImage } from "@/components/site/Reveal";
import { badgeClass } from "@/components/site/ExpeditionCard";
import { useLang, DIFFICULTY } from "@/lib/i18n";
import { L } from "@/lib/localize";
import { useAssets, tourCover } from "@/lib/assets";
import api from "@/lib/api";
import JsonLd from "@/components/site/JsonLd";
import { graph, breadcrumbNode, itemListNode } from "@/lib/schema";
import { useSeo } from "@/lib/seo";

export default function Expeditions() {
  const { t, lang } = useLang();
  const assets = useAssets();
  useSeo({ title: t("seo.tours.title"), description: t("seo.tours.desc"), path: "/viaggi" });
  const diffLabel = (d) => (DIFFICULTY[lang] && DIFFICULTY[lang][d]) || d;
  const [tours, setTours] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const jsonLd = useMemo(
    () =>
      graph([
        breadcrumbNode([{ name: "Home", path: "/" }, { name: t("nav.expeditions"), path: "/viaggi" }], lang),
        itemListNode({ lang, items: tours.map((x) => ({ name: L(x, lang).title, path: `/viaggi/${x.slug}` })) }),
      ]),
    [tours, lang, t]
  );

  useEffect(() => {
    api.get("/tours").then((r) => setTours(r.data)).catch(() => {}).finally(() => setLoaded(true));
  }, []);

  return (
    <div className="bg-obsidian text-cream min-h-screen">
      <Nav />
      <JsonLd id="jsonld-expeditions" data={jsonLd} />
      <section className="pt-32 sm:pt-44 pb-16">
        <div className="container-x">
          <Reveal>
            <p className="eyebrow mb-3">{t("tours.eyebrow")}</p>
            <h1 className="font-serif font-semibold text-5xl sm:text-7xl leading-[0.95]" data-testid="expeditions-title">{t("tours.title")}</h1>
          </Reveal>
        </div>
      </section>

      <section className="pb-24 sm:pb-32" data-testid="expeditions-grid">
        <div className="container-x space-y-20 sm:space-y-28">
          {tours.map((tour, i) => {
            const lt = L(tour, lang);
            const cover = tourCover(assets, tour);
            return (
              <Reveal key={tour.id}>
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-14 items-center">
                  <div className={"lg:col-span-7 " + (i % 2 ? "lg:order-2" : "")}>
                    <Link to={`/viaggi/${tour.slug}`} className="group block relative border border-copper/20 overflow-hidden" data-testid={`expedition-row-${tour.slug}`}>
                      <ParallaxImage src={cover} alt={lt.title} className="aspect-[16/10]" strength={50} imgClass="transition-transform duration-[1400ms] group-hover:scale-105" />
                      <div className="absolute top-4 left-4"><span className={badgeClass(tour.badge)}>{t(`badge.${tour.badge}`)}</span></div>
                      <div className="absolute inset-0 bg-copper/0 group-hover:bg-copper/10 mix-blend-overlay transition-colors duration-500 pointer-events-none" />
                    </Link>
                  </div>
                  <div className={"lg:col-span-5 " + (i % 2 ? "lg:order-1" : "")}>
                    <p className="font-mono text-copper text-sm tracking-[0.3em]">{String(i + 1).padStart(2, "0")}</p>
                    <h2 className="font-serif text-3xl sm:text-5xl text-cream mt-3 leading-[1.02]">
                      <Link to={`/viaggi/${tour.slug}`} className="hover:text-copperlight transition-colors">{lt.title}</Link>
                    </h2>
                    <p className="mt-4 text-cream2 leading-relaxed">{lt.excerpt}</p>
                    <div className="mt-5 flex flex-wrap gap-2.5">
                      <span className="chip"><CalendarDays className="h-3.5 w-3.5 text-copper" /> {lt.dates_label}</span>
                      <span className="chip"><Users className="h-3.5 w-3.5 text-copper" /> {t("tours.group")} {tour.group_max}</span>
                      <span className="chip"><Gauge className="h-3.5 w-3.5 text-copper" /> {diffLabel(tour.difficulty)}</span>
                    </div>
                    <div className="mt-6 flex items-center gap-6">
                      <div>
                        <p className="font-mono text-[10px] uppercase tracking-widest text-sand">{t("tours.from")}</p>
                        <p className="font-serif text-2xl text-copper">€{Number(tour.price).toLocaleString("it-IT")}</p>
                      </div>
                      <Link to={`/candidatura?tour=${tour.slug}`} className="btn-ghost !px-5 !py-2.5" data-testid={`expedition-open-${tour.slug}`}>
                        {t("tours.cta")} <ArrowRight className="h-4 w-4" />
                      </Link>
                    </div>
                  </div>
                </div>
              </Reveal>
            );
          })}
          {loaded && tours.length === 0 && (
            <p className="text-sand text-center py-20">{t("tours.sub")}</p>
          )}
        </div>
      </section>
      <Footer />
    </div>
  );
}
